import { z, ZodType } from "zod";
import { assert } from "~/lib/assert";

/* Helper functions to deal with submitting data through forms.
 *
 * To allow arbitrary data submission through forms, we JSON-ify the data and
 * store it in a "data" field of a FormData instance. Then, when parsing, we'll
 * do the process in reverse. We use Zod to make everything typesafe.
 */

export function parseForm<T extends ZodType>(
  formData: FormData,
  type: T,
): z.infer<T> {
  const data = formData.get("data");
  assert(data !== null && typeof data === "string", "Invalid", 400);

  let json: any;

  try {
    json = JSON.parse(data);
  } catch (e) {
    throw new Response("Invalid form data", {
      status: 400,
      statusText: "Could not parse form data from request",
    });
  }

  const result = type.safeParse(json);
  assert(result.success, "Invalid form data", 400);
  return result.data;
}

export function createFormData<T>(data: T) {
  const formData = new FormData();
  formData.set("data", JSON.stringify(data));
  return formData;
}

export async function parseRequestForm<T extends ZodType>(
  request: Request,
  type: T,
): Promise<z.infer<T>> {
  let formData: FormData;

  try {
    formData = await request.formData();
  } catch (e) {
    throw new Response("Invalid form data", {
      status: 400,
      statusText: "Could not parse form data from request",
    });
  }

  return parseForm(formData, type);
}
